import { useEffect, useState } from "react";
import { ExternalLink, Pencil, Save, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const formatUpdatedAt = (value) => {
  if (!value) return "Using starter FAQ";
  return `Last published ${new Date(value).toLocaleDateString("en-GB")}`;
};

export function SuperFaqPanel() {
  const [faq, setFaq] = useState(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/public/faq")
      .then(({ data }) => {
        setFaq(data);
        setDraft(data.content);
      })
      .catch((error) => toast.error(errMsg(error)));
  }, []);

  const openEditor = () => {
    setDraft(faq?.content || "");
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/super/faq", { content: draft });
      setFaq(data);
      setDraft(data.content);
      setEditing(false);
      toast.success("FAQ published");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b pb-8"
      data-testid="super-faq-panel"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          Service settings
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold">Frequently asked questions</h2>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="super-faq-status">
          {faq ? formatUpdatedAt(faq.updated_at) : "Loading FAQ…"}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline" data-testid="super-faq-view-link">
          <Link to="/faq">
            <ExternalLink className="mr-2 h-4 w-4" />
            View public page
          </Link>
        </Button>
        <Button
          size="sm"
          disabled={!faq}
          onClick={openEditor}
          data-testid="super-faq-edit-button"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit FAQ
        </Button>
      </div>
      <Dialog open={editing} onOpenChange={(open) => !open && setEditing(false)}>
        <DialogContent className="max-w-4xl" data-testid="super-faq-dialog">
          <DialogHeader>
            <DialogTitle>Edit frequently asked questions</DialogTitle>
          </DialogHeader>
          <Textarea
            className="min-h-[460px] resize-y font-mono text-sm leading-6"
            onChange={(event) => setDraft(event.target.value)}
            value={draft}
            data-testid="super-faq-editor-input"
          />
          <DialogFooter>
            <Button
              disabled={saving}
              variant="outline"
              onClick={() => setEditing(false)}
              data-testid="super-faq-cancel-button"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button disabled={saving} onClick={save} data-testid="super-faq-save-button">
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Publishing…" : "Publish FAQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}